import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import GroupedCheckboxSelect from '~/components/Shared/GroupedCheckboxSelect/GroupedCheckboxSelect';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { useThrottledValue } from '~/eds/src/hooks/useThrottledValue';
import { IconType } from '~/eds/src/types/IconType';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';

import { getPresets, groupFilterOptions } from './utils';

export type SelectedFilters = Record<string, FieldId[]>;

export type OptionsType = { section: string; id: string; label: string };

type Props = {
  buttonIcon?: IconType;
  buttonSize?: 'xs' | 's' | 'm' | 'l';
  buttonText?: string;
  buttonVariant?: 'action' | 'secondary';
  collapsible?: boolean;
  ctaText?: string;
  groupsOrder: string[];
  limit?: number;
  pinnedFiltersIds?: Array<PilotId | string>;
  preset?: 'add-filters-button' | 'add-filters' | 'edit-charts';
  searchFilters: OptionsType[];
  selectedFilters?: SelectedFilters;
  getActionButtonTooltip: (state: { selectedFiltersCount: number }) => string;
  onApply: (ids: string[]) => void;
  onOpenQueryBuilder?: () => void;
  onUpdatePinnedFilters?: (
    updatedPinnedFilters: Array<PilotId | string>,
  ) => void;
  renderHelpText?: (state: {
    selectedFiltersCount: number;
  }) => JSX.Element | null;
  renderLimitHeader?: () => JSX.Element;
};

export const ManageFilters = (props: Props) => {
  const intl = useIntl();

  const presets = getPresets(intl);

  const resolvePresetProps = (props: Props) => {
    const { preset } = props;
    const presetProps = preset
      ? presets[preset]
      : (presets['add-filters'] ?? {});
    return {
      ...presetProps,
      ...props,
    };
  };

  const {
    buttonText,
    buttonIcon,
    buttonSize,
    buttonVariant,
    collapsible = false,
    getActionButtonTooltip,
    groupsOrder,
    limit,
    pinnedFiltersIds,
    renderHelpText,
    renderLimitHeader,
    searchFilters,
    selectedFilters = {},
    onApply,
    onOpenQueryBuilder,
    onUpdatePinnedFilters,
  } = resolvePresetProps(props);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(false);
  const [selectedFilterIds, setSelectedFilterIds] =
    useState<Nullable<SelectedFilters>>(selectedFilters);

  const selectedFiltersCount = Object.values(selectedFilterIds || {}).flat()
    .length;
  const hasNoFiltersSelected = selectedFiltersCount === 0;
  const hasOverLimitSelected = Boolean(limit && selectedFiltersCount > limit);

  const throttleMs = useMemo(() => searchFilters.length * 2, [searchFilters]);
  const throttledSearch = useThrottledValue(search, throttleMs);

  const handleShowModal = () => {
    setVisible(true);
    setSelectedFilterIds(selectedFilters);
  };

  const handleHide = () => {
    setSearch('');
    setVisible(false);
  };

  const handleSearch = (updatedSearch: Nullable<string>) => {
    setSearch(updatedSearch || '');
  };

  const generateFilterIdsInNewOrder = () => {
    const existingIDs = Object.values(selectedFilters).flat();
    const allIDs = Object.values(selectedFilterIds || {}).flat();
    const newlyAddedIDs = allIDs.filter((item) => !existingIDs.includes(item));
    const remainingIDs = allIDs.filter((item) => !newlyAddedIDs.includes(item));
    return [...newlyAddedIDs, ...remainingIDs];
  };

  const handleSubmit = () => {
    const ids = generateFilterIdsInNewOrder();
    onApply(ids);
    handleHide();
  };

  const checkboxGroups = React.useMemo(() => {
    const groupedFieldOptions = groupFilterOptions({
      searchFilters,
      search,
      groupsOrder,
    });
    const optionsCount = groupedFieldOptions.reduce(
      (acc, group) => acc + group.options.length,
      0,
    );

    if (optionsCount === 0) {
      return (
        <Text preset="description">
          {intl.formatMessage({
            id: 'ManageFilters.noMatchingFields',
            defaultMessage: 'No matching fields.',
            description: 'Message when no fields match the search',
          })}
        </Text>
      );
    }

    return (
      <GroupedCheckboxSelect<FieldId>
        collapsible={collapsible}
        pins={pinnedFiltersIds as string[]}
        search={throttledSearch}
        optionGroups={groupedFieldOptions}
        value={selectedFilterIds}
        onChange={setSelectedFilterIds}
        onUpdatePinnedFilters={onUpdatePinnedFilters}
      />
    );
  }, [searchFilters, selectedFilterIds, throttledSearch, groupsOrder]);

  return (
    <>
      <Button
        iconPosition="left"
        size={buttonSize}
        icon={buttonIcon}
        text={buttonText}
        variant={buttonVariant}
        onClick={handleShowModal}
      />
      {visible && (
        <Panel
          width="l"
          enableBackdrop
          mode="floating"
          position="right"
          enableContentPadding={false}
          hidden={{
            isHidden: !visible,
            onHide: handleHide,
          }}
          title={intl.formatMessage({
            id: 'ManageFilters.filtersTitle',
            defaultMessage: 'Filters',
            description: 'Title for filters panel',
          })}
          footer={{
            leftActions: onOpenQueryBuilder
              ? [
                  {
                    level: 'action',
                    text: intl.formatMessage({
                      id: 'ManageFilters.advancedQueryBuilder',
                      defaultMessage: 'Advanced Query Builder',
                      description: 'Text for advanced query builder button',
                    }),
                    icon: 'plus-circle',
                    iconPosition: 'left',
                    onClick: onOpenQueryBuilder,
                  },
                ]
              : [],
            actions: [
              {
                text: intl.formatMessage({
                  id: 'ManageFilters.cancelButton',
                  defaultMessage: 'Cancel',
                  description: 'Cancel button text',
                }),
                onClick: handleHide,
              },
              {
                disabled: hasNoFiltersSelected || hasOverLimitSelected,
                text: intl.formatMessage({
                  id: 'ManageFilters.addFiltersButtonText',
                  defaultMessage: 'Add Filters',
                  description: 'Add filters button text',
                }),
                level: 'primary',
                tooltip: getActionButtonTooltip({ selectedFiltersCount }),
                onClick: handleSubmit,
              },
            ],
          }}
        >
          <Layout direction="column" h="100%" spacing={6} w="100%" py={4}>
            {renderLimitHeader?.()}
            <Box flex="none" px={4}>
              <SearchInput
                autoFocus
                name="fields"
                placeholder={intl.formatMessage({
                  id: 'ManageFilters.searchPlaceholder',
                  defaultMessage: 'Search by fields…',
                  description: 'Placeholder text for fields search input',
                })}
                value={search}
                onChange={handleSearch}
              />
            </Box>
            {renderHelpText?.({ selectedFiltersCount })}
            <Box flex="auto" overflowY="auto" px={4}>
              {checkboxGroups}
            </Box>
          </Layout>
        </Panel>
      )}
    </>
  );
};
